import Skeleton from "react-loading-skeleton";
import cx from "classnames";

import SpaceliftLogo from "ds/components/SpaceliftLogo/Logo";
import PageWrapper from "components/PageWrapper";
import Box from "ds/components/Box";
import { selectNavigationIsExpanded } from "components/MainNavigation/selectors/navigationState";
import useMainNavigationStore from "components/MainNavigation/stores/useMainNavigationStore";

import styles from "./styles.module.css";

type GroupProps = {
  itemWidth?: number;
  isExpanded?: boolean;
};

const Group = ({ itemWidth, isExpanded }: GroupProps) => (
  <Box gap="medium" align="center" padding={isExpanded ? "small" : "small 0"}>
    <Skeleton circle width={24} height={24} />
    {isExpanded && <Skeleton width={itemWidth} height={16} borderRadius={8} />}
  </Box>
);

const SkeletonLoader = ({ initial }: { initial?: boolean }) => {
  const isSidebarExpanded = useMainNavigationStore(selectNavigationIsExpanded);

  return (
    <PageWrapper className={styles.layout}>
      <Box>
        <Box
          direction="column"
          justify="between"
          className={cx(styles.mainNavigation, initial && styles.initial, {
            [styles.expanded]: isSidebarExpanded,
          })}
        >
          <Box direction="column" gap="medium" padding="small">
            <Box fullWidth justify="between" align="center">
              <SpaceliftLogo size="small" className={styles.navigationLogo} />

              {isSidebarExpanded && (
                <Box gap="medium">
                  <Skeleton width={16} height={16} borderRadius={4} />
                  <Skeleton width={16} height={16} borderRadius={4} />
                </Box>
              )}
            </Box>

            <Box
              gap="medium"
              align="center"
              margin="small 0 0 0"
              padding={isSidebarExpanded ? "small" : "0"}
            >
              <Skeleton width={isSidebarExpanded ? 210 : 24} height={24} borderRadius={8} />
            </Box>
            <Group itemWidth={60} isExpanded={isSidebarExpanded} />
            <Box gap="medium" align="center">
              <Skeleton width={isSidebarExpanded ? 210 : 24} height={1} />
            </Box>
            <Group itemWidth={120} isExpanded={isSidebarExpanded} />
            <Group itemWidth={128} isExpanded={isSidebarExpanded} />
            <Group itemWidth={112} isExpanded={isSidebarExpanded} />
            <Group itemWidth={160} isExpanded={isSidebarExpanded} />
            <Group itemWidth={80} isExpanded={isSidebarExpanded} />
            <Group itemWidth={128} isExpanded={isSidebarExpanded} />
          </Box>

          <Box direction="column" gap="medium" padding="small">
            <Box gap="medium" align="center" padding={isSidebarExpanded ? "small" : "0"}>
              <Skeleton width={isSidebarExpanded ? 210 : 24} height={1} />
            </Box>
            <Group itemWidth={112} isExpanded={isSidebarExpanded} />
            <Group itemWidth={160} isExpanded={isSidebarExpanded} />
            <Group itemWidth={80} isExpanded={isSidebarExpanded} />

            <Box gap="medium" align="center" margin="small 0 0 0" padding="small 0">
              <Skeleton circle width={32} height={32} className={styles.userCard} />
              {isSidebarExpanded && (
                <Box gap="small" direction="column">
                  <Skeleton width={128} height={16} borderRadius={8} />
                  <Skeleton width={104} height={8} borderRadius={4} />
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          direction="column"
          className={cx(styles.content, {
            [styles.expanded]: isSidebarExpanded,
          })}
        >
          <Box
            justify="between"
            align="center"
            className={cx(styles.header, initial && styles.initial)}
          >
            <Skeleton width={156} height={20} borderRadius={10} />
            <Box gap="medium">
              <Skeleton width={202} height={34} borderRadius={17} />
              <Skeleton width={68} height={34} borderRadius={17} />
              <Skeleton width={68} height={34} borderRadius={17} />
            </Box>
          </Box>

          <SpaceliftLogo className={styles.logo} size="large" />
        </Box>
      </Box>
    </PageWrapper>
  );
};

export default SkeletonLoader;
